import { Injectable, inject } from '@angular/core';

import { Store } from '@ngrx/store';

import { EMPTY } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  skip,
  switchMap,
} from 'rxjs/operators';

import isEqual from 'lodash-es/isEqual';

import { selectFeed, selectFeedSettings } from '../reducers';
import { FeedApiService } from 'src/app/api/services/feed-api.service';

/**
 * Autosave for the feed's settings: watch the client state where it lives (the
 * feed reducer) and persist every change. Not an action-triggered write - the
 * settings are edited by a dozen small dispatches - so the owner is this
 * store-subscribing pipeline, started by `feed.component` injecting the service.
 *
 * Success is silent (the applied setting is the feedback); a failed save is
 * logged and the next change simply retries.
 */
@Injectable({ providedIn: 'root' })
export class FeedSettingsAutosaveService {
  private readonly store = inject(Store);
  private readonly feedApi = inject(FeedApiService);

  constructor() {
    this.store
      .select(selectFeed)
      .pipe(
        // Ensure we actually have a feed before listening to settings
        filter((feed): feed is NonNullable<typeof feed> => !!feed?.id),
        distinctUntilChanged((a, b) => isEqual(a.id, b.id)),
        // Switch to the settings stream for THIS specific feed
        switchMap((feed) =>
          this.store.select(selectFeedSettings).pipe(
            // Skip the very first emission that happens when the feed loads
            skip(1),
            distinctUntilChanged((a, b) => isEqual(a, b)),
            switchMap((feedSettings) =>
              this.feedApi.saveFeedSettings(feed.id, feedSettings).pipe(
                // ! per-save catch - an error must not kill the autosave stream
                catchError((error) => {
                  console.error('Failed to save feed settings', error);
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      )
      .subscribe();
  }
}
